import styled, { keyframes } from "styled-components";
import { colors } from "../../../styles/colors";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const PromptOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(18, 34, 67, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  direction: rtl;
  animation: ${fadeIn} 0.2s ease-out;
`;

export const PromptContent = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 32px;
  max-width: 400px;
  width: 100%;
  position: relative;
  box-shadow: 0 20px 40px rgba(18, 34, 67, 0.3);
  animation: ${slideUp} 0.3s ease-out;
  text-align: center;

  @media (max-width: 768px) {
    padding: 24px;
    max-width: 90%;
  }
`;

export const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, ${colors.gradientTeal}, ${colors.gradientBlue});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

export const PromptTitle = styled.h2`
  font-family: "Noto Sans Hebrew", sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.darkBlue};
  margin: 0 0 8px 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

export const PromptDescription = styled.p`
  font-family: "Noto Sans Hebrew", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.blueGray};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const PromptButton = styled.button<{ $variant: "primary" | "secondary" }>`
  font-family: "Noto Sans Hebrew", sans-serif;
  padding: 10px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
  border: none;
  flex: 1;

  ${({ $variant }) =>
    $variant === "primary"
      ? `
    background: linear-gradient(135deg, ${colors.gradientTeal}, ${colors.gradientBlue});
    color: ${colors.white};
    box-shadow: 0 4px 12px rgba(0, 78, 247, 0.3);

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 20px rgba(0, 78, 247, 0.4);
    }
  `
      : `
    background: ${colors.lightGray};
    color: ${colors.darkBlue};
    border: 1px solid ${colors.lightGrayBlue};

    &:hover:not(:disabled) {
      background: ${colors.lightGrayBlue};
      transform: translateY(-1px);
    }
  `}

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 480px) {
    min-width: 100%;
  }
`;

export const StatusMessage = styled.div<{ $type: "success" | "error" }>`
  font-family: "Noto Sans Hebrew", sans-serif;
  font-size: 14px;
  margin-top: 16px;
  text-align: center;
  color: ${({ $type }) => ($type === "success" ? colors.green : colors.red)};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  display: inline-block;
`;
